import React, { useState, useEffect } from 'react';
import { fieldKey } from '../fieldKeys';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Card, Checkbox, Dialog, EmptyState, IconButton, PageHeader, Pill, Select, Skeleton, TextField, Textarea,
  useConfirm, useToast,
} from '../ui';
import { EditIcon, LinkIcon, PlusIcon, RefreshIcon, SearchIcon, TrashIcon } from '../ui/icons';
import { sectionCrumb } from '../settings/SectionLayout';
import FieldMappingBuilder from './FieldMappingBuilder';

/**
 * /admin/agents — completion agents (deprecated since slice 7b: the TMDB
 * integrations replaced them), kept for administrators: list, test,
 * duplicate, edit, delete.
 */
function CompletionAgentConfiguration() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [agents, setAgents] = useState([]);
  const [inventoryTypes, setInventoryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);

  // Agents store field keys; show the field's name, the key in a tooltip.
  const fieldLabel = (agent, key) => {
    const type = inventoryTypes.find(it => it.id === agent.inventory_type_id);
    const field = (type?.fields || []).find(f => fieldKey(f) === key);
    return <span title={key}>{field ? field.name : key}</span>;
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/completion-agents');
      setAgents(response.data);
    } catch (err) {
      console.error('Error fetching completion agents:', err);
      setError(t('agentConfig.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    api.get('/item-types').then(response => setInventoryTypes(response.data)).catch(err => console.error('Error fetching item types:', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, on arrival

  const handleDeleteAgent = async (agent) => {
    if (!(await confirm({
      title: t('agentConfig.confirmDeleteTitle'),
      message: t('agentConfig.confirmDelete', { name: agent.name }),
      confirmLabel: t('agentConfig.deleteButton'),
      danger: true,
    }))) return;
    try {
      await api.delete(`/completion-agents/${agent.id}`);
      fetchAgents();
    } catch (err) {
      console.error('Error deleting completion agent:', err);
      setError(t('agentConfig.errors.delete'));
    }
  };

  const handleDuplicateAgent = async (id) => {
    try {
      await api.post(`/completion-agents/${id}/duplicate`);
      fetchAgents();
    } catch (err) {
      console.error('Error duplicating completion agent:', err);
      setError(t('agentConfig.errors.duplicate'));
    }
  };

  const handleTestAgent = async (id) => {
    try {
      const response = await api.get(`/completion-agents/${id}/test`);
      if (response.data.success) toast.success(t('agentConfig.testOk'));
      else toast.error(response.data.error, { title: t('agentConfig.testFailed') });
    } catch (err) {
      console.error('Error testing completion agent:', err);
      toast.error(err.response?.data?.error || err.message, { title: t('agentConfig.testFailed') });
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAgent(null);
    fetchAgents();
  };
  const create = () => { setEditingAgent(null); setShowModal(true); };

  const header = (
    <PageHeader
      breadcrumb={[sectionCrumb(t, 'admin'), { label: t('nav.completionAgentsDeprecated') }]}
      title={t('agentConfig.title')}
      actions={<Button icon={PlusIcon} onClick={create}>{t('agentConfig.add')}</Button>}
    />
  );
  if (loading) return <>{header}<Skeleton height={240} radius="var(--radius-lg)" /></>;

  return (
    <div>
      {header}
      <div className="settings-stack">
        <Alert tone="warning">{t('agentConfig.deprecated')}</Alert>
        {error && <Alert tone="danger">{error}</Alert>}
        {agents.length === 0 ? (
          <EmptyState icon={LinkIcon} title={t('agentConfig.emptyTitle')} action={<Button icon={PlusIcon} onClick={create}>{t('agentConfig.add')}</Button>}>
            {t('agentConfig.empty')}
          </EmptyState>
        ) : (
          <ul className="place-types">
            {agents.map((agent) => (
              <li key={agent.id}>
                <Card as="article" padding="lg" aria-labelledby={`agent-${agent.id}`} className="agent-card">
                  <div className="settings-card__head">
                    <div>
                      <h2 id={`agent-${agent.id}`} className="place-type__title">
                        {agent.name} {agent.is_active ? <Pill tone="green">{t('agentConfig.active')}</Pill> : <Pill>{t('agentConfig.inactive')}</Pill>}
                      </h2>
                      {agent.description && <p className="settings-muted">{agent.description}</p>}
                    </div>
                    <div className="accounts__actions">
                      <Button size="sm" variant="secondary" icon={RefreshIcon} onClick={() => handleTestAgent(agent.id)}>{t('agentConfig.test')}</Button>
                      <Button size="sm" variant="secondary" icon={EditIcon} onClick={() => { setEditingAgent(agent); setShowModal(true); }}
                              aria-label={t('agentConfig.editNamed', { name: agent.name })}>{t('common.edit')}</Button>
                      <Button size="sm" variant="secondary" icon={PlusIcon} onClick={() => handleDuplicateAgent(agent.id)}
                              aria-label={t('agentConfig.duplicateNamed', { name: agent.name })}>{t('itemTypes.duplicate')}</Button>
                      <IconButton icon={TrashIcon} size="sm" variant="ghost" className="field-row__remove"
                                  label={t('agentConfig.deleteNamed', { name: agent.name })} onClick={() => handleDeleteAgent(agent)} />
                    </div>
                  </div>
                  <dl className="agent-card__facts">
                    <div><dt>{t('agentConfig.itemType')}</dt><dd>{agent.inventory_type_name || t('agentConfig.unknown')}</dd></div>
                    <div><dt>{t('agentConfig.url')}</dt><dd className="agent-card__url">{agent.api_url}</dd></div>
                    <div><dt>{t('agentConfig.searchField')}</dt><dd>{fieldLabel(agent, agent.search_field)}</dd></div>
                    <div><dt>{t('agentConfig.method')}</dt><dd>{agent.request_method}</dd></div>
                    <div><dt>{t('agentConfig.key')}</dt><dd>{agent.has_api_key ? agent.api_key_masked : t('agentConfig.noKey')}</dd></div>
                  </dl>
                  {Object.keys(agent.field_mappings || {}).length > 0 && (
                    <div className="agent-card__mappings">
                      <p className="ui-field__label">{t('mappingBuilder.title')}</p>
                      <ul>
                        {Object.entries(agent.field_mappings).map(([field, path]) => (
                          <li key={field}><strong>{fieldLabel(agent, field)}</strong> ← <code>{path}</code></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showModal && <CompletionAgentModal agent={editingAgent} inventoryTypes={inventoryTypes} onClose={handleModalClose} />}
    </div>
  );
}

/** Creating or editing a completion agent: the API, the paths of its answer, the field mappings. */
export function CompletionAgentModal({ agent, inventoryTypes, onClose }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    inventory_type_id: '',
    api_url: '',
    api_key: '',
    request_method: 'GET',
    request_headers: {},
    request_body_template: '',
    response_path: '',
    multiple_results_path: '',
    result_display_field: '',
    field_mappings: {},
    search_field: '',
    is_active: true
  });
  const [selectedInventoryType, setSelectedInventoryType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sampleResponse, setSampleResponse] = useState(null);
  const [availablePaths, setAvailablePaths] = useState([]);
  const [fetchingSample, setFetchingSample] = useState(false);
  const [sampleSearchValue, setSampleSearchValue] = useState('test');

  // Helper function to safely parse fields
  const getInventoryTypeFields = (inventoryType) => {
    if (!inventoryType || !inventoryType.fields) return [];
    
    // If it's already an array, return it
    if (Array.isArray(inventoryType.fields)) {
      return inventoryType.fields;
    }
    
    // If it's a string, parse it
    if (typeof inventoryType.fields === 'string') {
      try {
        return JSON.parse(inventoryType.fields);
      } catch (e) {
        console.error('Error parsing inventory type fields:', e);
        return [];
      }
    }
    
    return [];
  };

  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        description: agent.description || '',
        inventory_type_id: agent.inventory_type_id || '',
        api_url: agent.api_url || '',
        api_key: '', // Don't prefill API key for security
        request_method: agent.request_method || 'GET',
        request_headers: agent.request_headers || {},
        request_body_template: agent.request_body_template || '',
        response_path: agent.response_path || '',
        multiple_results_path: agent.multiple_results_path || '',
        result_display_field: agent.result_display_field || '',
        field_mappings: agent.field_mappings || {},
        search_field: agent.search_field || '',
        is_active: agent.is_active !== undefined ? agent.is_active : true
      });
      
      // Find and set the selected inventory type
      const inventoryType = inventoryTypes.find(type => type.id === agent.inventory_type_id);
      setSelectedInventoryType(inventoryType || null);
    }
  }, [agent, inventoryTypes]);

  const handleInputChange = async (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'inventory_type_id') {
      // Another item type has other fields: its mappings and search field go, once confirmed.
      if (Object.keys(formData.field_mappings || {}).length > 0 && !(await confirm({
        title: t('agentConfig.form.confirmTypeTitle'),
        message: t('agentConfig.form.confirmType', { count: Object.keys(formData.field_mappings).length }),
        confirmLabel: t('agentConfig.form.confirmTypeButton'),
        danger: true,
      }))) return;
      const inventoryType = inventoryTypes.find(it => it.id === parseInt(value, 10));
      setSelectedInventoryType(inventoryType || null);
      setFormData(prev => ({ ...prev, inventory_type_id: value, field_mappings: {}, search_field: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (agent) {
        await api.put(`/completion-agents/${agent.id}`, formData);
      } else {
        await api.post('/completion-agents', formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving completion agent:', error);
      setError(error.response?.data?.error || t('agentConfig.errors.save'));
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (templateName) => {
    if (templateName === 'themoviedb') {
      setFormData(prev => ({
        ...prev,
        name: 'The Movie Database',
        description: 'Fetch movie information from TheMovieDB API',
        api_url: 'https://api.themoviedb.org/3/search/movie?query={search_value}',
        request_method: 'GET',
        request_headers: {
          'Authorization': 'Bearer {api_key}'
        },
        response_path: '',
        multiple_results_path: 'results',
        result_display_field: 'title',
        field_mappings: selectedInventoryType ? getDefaultMovieFieldMappings() : {},
        search_field: getDefaultSearchField()
      }));
    }
  };

  // The key of the type's title-like field, if any (agents store field keys).
  const getDefaultSearchField = () => {
    const fields = selectedInventoryType ? getInventoryTypeFields(selectedInventoryType) : [];
    const field = fields.find(f => /title|titre|name|nom/i.test(f.name || '')) || fields[0];
    return field ? fieldKey(field) : '';
  };

  const getDefaultMovieFieldMappings = () => {
    if (!selectedInventoryType) return {};
    
    const mappings = {};
    const fields = getInventoryTypeFields(selectedInventoryType);
    
    // Create default mappings for common movie fields
    fields.forEach(field => {
      const fieldName = field.name.toLowerCase();
      if (fieldName.includes('title') || fieldName.includes('name')) {
        mappings[fieldKey(field)] = 'title';
      } else if (fieldName.includes('year') || fieldName.includes('date')) {
        mappings[fieldKey(field)] = 'release_date';
      } else if (fieldName.includes('overview') || fieldName.includes('description') || fieldName.includes('summary')) {
        mappings[fieldKey(field)] = 'overview';
      } else if (fieldName.includes('rating') || fieldName.includes('score')) {
        mappings[fieldKey(field)] = 'vote_average';
      }
    });
    
    return mappings;
  };

  const fetchSampleResponse = async () => {
    if (!formData.api_url) {
      setError(t('agentConfig.form.urlFirst'));
      return;
    }

    setFetchingSample(true);
    setError('');

    try {
      // For existing agents, use the agent's stored API key if no new one is provided
      let apiKeyToUse = formData.api_key;
      if (!apiKeyToUse && agent && agent.has_api_key) {
        // For existing agents with stored API keys, let the backend use the stored key
        apiKeyToUse = 'USE_STORED_KEY';
      }

      const response = await api.post('/completion-agents/sample-response', {
        api_url: formData.api_url,
        request_method: formData.request_method,
        request_headers: formData.request_headers,
        request_body_template: formData.request_body_template,
        api_key: apiKeyToUse,
        sample_search_value: sampleSearchValue,
        agent_id: agent ? agent.id : null // Send agent ID so backend can use stored key
      });

      if (response.data.success) {
        setSampleResponse(response.data.sample_response);
        setAvailablePaths(response.data.available_paths);
        
        // A new sample has its own paths: those chosen go. The field mappings stay (they name paths, not this sample).
        setFormData(prev => ({
          ...prev,
          response_path: '',
          multiple_results_path: '',
          result_display_field: '',
        }));
      } else {
        setError(t('agentConfig.errors.sample', { error: response.data.error }));
      }
    } catch (error) {
      console.error('Error fetching sample response:', error);
      setError(t('agentConfig.errors.sample', { error: error.response?.data?.error || error.message }));
    } finally {
      setFetchingSample(false);
    }
  };

  const types = selectedInventoryType ? getInventoryTypeFields(selectedInventoryType) : [];
  const describePath = (pathInfo) => `${pathInfo.path} (${pathInfo.type})${pathInfo.description ? ` — ${pathInfo.description}` : ''}`;

  return (
    <Dialog
      open
      onClose={onClose}
      title={agent ? t('agentConfig.form.editTitle') : t('agentConfig.form.addTitle')}
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="completion-agent" loading={loading}>{agent ? t('common.save') : t('agentConfig.form.create')}</Button>
        </>
      )}
    >
      <form id="completion-agent" className="dialog-form" onSubmit={handleSubmit}>
        {!agent && (
          <div className="dialog-preview">
            <p className="dialog-preview__title">{t('agentConfig.form.templates')}</p>
            <Button variant="secondary" size="sm" onClick={() => loadTemplate('themoviedb')}>{t('agentConfig.form.tmdbTemplate')}</Button>
          </div>
        )}
        <TextField name="name" label={t('agentConfig.form.name')} value={formData.name} onChange={handleInputChange} required
                   placeholder={t('agentConfig.form.namePlaceholder')} />
        <Select name="inventory_type_id" label={t('agentConfig.itemType')} value={formData.inventory_type_id} onChange={handleInputChange} required>
          <option value="">{t('agentConfig.form.chooseType')}</option>
          {inventoryTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
        </Select>
        <Select name="search_field" label={t('agentConfig.searchField')} value={formData.search_field} onChange={handleInputChange} required
                disabled={!selectedInventoryType} hint={!selectedInventoryType ? t('agentConfig.form.typeFirst') : undefined}>
          <option value="">{t('agentConfig.form.chooseField')}</option>
          {types.map((field) => <option key={fieldKey(field)} value={fieldKey(field)}>{field.name} ({field.type})</option>)}
        </Select>
        <Textarea name="description" label={t('agentConfig.form.description')} rows={2} value={formData.description}
                  onChange={handleInputChange} placeholder={t('agentConfig.form.descriptionPlaceholder')} />
        <TextField name="api_url" type="url" label={t('agentConfig.url')} value={formData.api_url} onChange={handleInputChange} required
                   hint={t('agentConfig.form.urlHelp', { placeholder: '{search_value}' })} placeholder="https://api.example.com/search?q={search_value}" />
        <div className="type-editor__pair">
          <Select name="request_method" label={t('agentConfig.method')} value={formData.request_method} onChange={handleInputChange}>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
          </Select>
          <TextField name="api_key" type="password" autoComplete="off" label={t('agentConfig.key')} value={formData.api_key}
                     onChange={handleInputChange} placeholder={agent ? t('agentConfig.form.keyNew') : t('agentConfig.form.keyIfNeeded')}
                     hint={agent && agent.has_api_key && !formData.api_key
                       ? t('agentConfig.form.keyStored', { end: agent.api_key_masked?.slice(-3) || '***' }) : undefined} />
        </div>

        <fieldset className="agent-form__group">
          <legend className="ui-field__label">{t('agentConfig.form.responsePath')}</legend>
          <p className="ui-field__hint">{t('agentConfig.form.responsePathHelp')}</p>
          <div className="type-editor__add-option">
            <TextField label={t('agentConfig.form.sampleValue')} hideLabel value={sampleSearchValue}
                       onChange={(e) => setSampleSearchValue(e.target.value)} placeholder={t('agentConfig.form.sampleValue')} />
            <Button variant="secondary" icon={SearchIcon} onClick={fetchSampleResponse} disabled={!formData.api_url} loading={fetchingSample}>
              {t('agentConfig.form.fetchSample')}
            </Button>
          </div>
          {availablePaths.length > 0 && (
            <Select name="response_path" label={t('agentConfig.form.responsePathChoose')} value={formData.response_path} onChange={handleInputChange}>
              <option value="">{t('agentConfig.form.chooseResponsePath')}</option>
              {availablePaths.map((pathInfo) => <option key={pathInfo.path} value={pathInfo.path}>{describePath(pathInfo)}</option>)}
            </Select>
          )}
          {!sampleResponse && (
            <>
              <p className="ui-field__hint">{t('agentConfig.form.sampleHelp')}</p>
              {formData.api_url.includes('themoviedb.org') && !formData.api_key && !(agent && agent.has_api_key) && (
                <p className="ui-field__hint is-warning">{t('agentConfig.form.tmdbKey')}</p>
              )}
            </>
          )}
        </fieldset>

        <fieldset className="agent-form__group">
          <legend className="ui-field__label">{t('agentConfig.form.multiple')}</legend>
          {availablePaths.length > 0 ? (
            <Select name="multiple_results_path" label={t('agentConfig.form.multiplePath')} value={formData.multiple_results_path} onChange={handleInputChange}>
              <option value="">{t('agentConfig.form.singleOnly')}</option>
              {availablePaths.filter(p => p.type === 'array' && !p.path.includes('.*')).map((p) => (
                <option key={p.path} value={p.path}>{p.path}{p.description ? ` — ${p.description}` : ''}</option>
              ))}
            </Select>
          ) : (
            <TextField name="multiple_results_path" label={t('agentConfig.form.multiplePath')} value={formData.multiple_results_path}
                       onChange={handleInputChange} placeholder={t('agentConfig.form.multiplePathPlaceholder')} />
          )}
          {availablePaths.length > 0 ? (
            <Select name="result_display_field" label={t('agentConfig.form.displayField')} value={formData.result_display_field} onChange={handleInputChange}>
              <option value="">{t('agentConfig.form.numbering')}</option>
              {availablePaths.filter(p => p.type !== 'array').map((p) => <option key={p.path} value={p.path}>{describePath(p)}</option>)}
            </Select>
          ) : (
            <TextField name="result_display_field" label={t('agentConfig.form.displayField')} value={formData.result_display_field}
                       onChange={handleInputChange} placeholder={t('agentConfig.form.displayFieldPlaceholder')} />
          )}
          <p className="ui-field__hint">{t('agentConfig.form.multipleHelp')}</p>
        </fieldset>

        {selectedInventoryType && (
          <FieldMappingBuilder
            fieldMappings={formData.field_mappings}
            onMappingsChange={(mappings) => setFormData(prev => ({ ...prev, field_mappings: mappings }))}
            sampleResponse={sampleResponse}
            inventoryFields={types}
          />
        )}

        {sampleResponse && (
          <div className="dialog-preview">
            <p className="dialog-preview__title">{t('agentConfig.form.sampleTitle', { value: sampleSearchValue })}</p>
            <pre className="agent-form__sample">{JSON.stringify(sampleResponse, null, 2)}</pre>
          </div>
        )}

        <Checkbox name="is_active" label={t('agentConfig.active')} checked={!!formData.is_active} onChange={handleInputChange} />
        {error && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}

export default CompletionAgentConfiguration;